import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
  Chip,
  Button,
  Grid,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import GradeOutlinedIcon from '@mui/icons-material/GradeOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import moment from 'moment';
import ImagePreviewModal, {
  getProductImagesFromRow,
  getProductVideosFromRow,
} from './ImagePreviewModal';

const ACCENT = '#7028DA';
const ACCENT_LIGHT = 'rgba(112, 40, 218, 0.08)';

const SpecCard = ({ icon: Icon, label, value }) => {
  if (value === null || value === undefined || value === '') return null;

  return (
    <Grid item xs={6}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          height: '100%',
          borderRadius: 2,
          bgcolor: '#fafafa',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'border-color 0.2s',
          '&:hover': { borderColor: 'rgba(112, 40, 218, 0.35)' },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: ACCENT_LIGHT,
              color: ACCENT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Box>
          <Box minWidth={0}>
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={700} noWrap title={String(value)}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );
};

const ProductDetailModal = ({ open, onClose, product, categoryName }) => {
  const [mediaOpen, setMediaOpen] = useState(false);

  const images = useMemo(() => (product ? getProductImagesFromRow(product) : []), [product]);
  const videos = useMemo(() => (product ? getProductVideosFromRow(product) : []), [product]);
  const thumb = images[0];
  const hasMedia = images.length > 0 || videos.length > 0;
  const mediaCount = images.length + videos.length;

  const handleClose = () => {
    setMediaOpen(false);
    onClose();
  };

  if (!product) return null;

  const bookNowValue =
    product.booknow != null ? (product.booknow ? 'Enabled' : 'Disabled') : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(112, 40, 218, 0.18)',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            background: `linear-gradient(135deg, ${ACCENT} 0%, #9b59e8 100%)`,
            color: '#fff',
            position: 'relative',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Inventory2OutlinedIcon />
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                Product Details
              </Typography>
              {categoryName && (
                <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap display="block">
                  {categoryName}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton
            onClick={handleClose}
            aria-label="close"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'linear-gradient(180deg, #f3f0ff 0%, #fafafa 100%)',
              minHeight: 220,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {thumb ? (
              <Box
                component="img"
                src={thumb}
                alt={product.name}
                onClick={hasMedia ? () => setMediaOpen(true) : undefined}
                sx={{
                  width: '100%',
                  maxHeight: 280,
                  objectFit: 'contain',
                  display: 'block',
                  cursor: hasMedia ? 'pointer' : 'default',
                  transition: 'transform 0.2s',
                  ...(hasMedia && {
                    '&:hover': { transform: 'scale(1.02)' },
                  }),
                }}
              />
            ) : (
              <Stack alignItems="center" spacing={1} py={5}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: ACCENT_LIGHT,
                    color: ACCENT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ImageNotSupportedOutlinedIcon sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  No image
                </Typography>
              </Stack>
            )}

            {hasMedia && (
              <Button
                size="small"
                startIcon={<PhotoLibraryOutlinedIcon />}
                onClick={() => setMediaOpen(true)}
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  textTransform: 'none',
                  bgcolor: '#fff',
                  color: ACCENT,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#fff', boxShadow: '0 6px 16px rgba(112,40,218,0.2)' },
                }}
              >
                Gallery{mediaCount > 1 ? ` (${mediaCount})` : ''}
              </Button>
            )}
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {product.name || 'N/A'}
            </Typography>

            {product.desc && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 2.5,
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  borderLeft: `3px solid ${ACCENT}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {product.desc}
                </Typography>
              </Paper>
            )}

            <Grid container spacing={1.5}>
              <SpecCard
                icon={ScaleOutlinedIcon}
                label="Weight"
                value={product.metal_weight ? `${product.metal_weight} g` : null}
              />
              <SpecCard
                icon={DiamondOutlinedIcon}
                label="Metal"
                value={product.metalName || product.metalname || product.metal}
              />
              <SpecCard icon={GradeOutlinedIcon} label="Karat" value={product.karat || product.carat} />
              <SpecCard icon={VerifiedOutlinedIcon} label="Purity" value={product.purity} />
              {bookNowValue && (
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      height: '100%',
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          bgcolor: ACCENT_LIGHT,
                          color: ACCENT,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ShoppingBagOutlinedIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Book Now
                        </Typography>
                        <Chip
                          label={bookNowValue}
                          size="small"
                          color={product.booknow ? 'success' : 'default'}
                          sx={{ fontWeight: 600, height: 22, mt: 0.25 }}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              )}
              <SpecCard
                icon={CalendarTodayOutlinedIcon}
                label="Created"
                value={product.createdAt ? moment(product.createdAt).format('DD MMM YYYY') : null}
              />
            </Grid>

            {product.tags?.length > 0 && (
              <Box mt={2.5}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                  Tags
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {product.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: ACCENT_LIGHT,
                        color: ACCENT,
                        fontWeight: 600,
                        border: '1px solid rgba(112, 40, 218, 0.2)',
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <ImagePreviewModal
        open={mediaOpen}
        handleClose={() => setMediaOpen(false)}
        images={images}
        videos={videos}
        imageUrl={images[0]}
        productName={product.name || ''}
        title="Product Media"
      />
    </>
  );
};

export default ProductDetailModal;
